import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { token_api } from "../../Utils/Network";

const cardSx = {
  background: "#fff",
  borderRadius: "14px",
  boxShadow: "0 1px 3px rgba(16,24,40,.06), 0 1px 2px rgba(16,24,40,.08)",
  border: "1px solid #eef0f4",
  p: { xs: 2, md: 3 },
  mb: 3,
};

const emptyTemplate = {
  name: "",
  package: "",
  report_number: 1,
  follow_up: false,
  pdf_layout: "explore",
};

// Must match ReportTemplate.PdfLayout on the backend.
const PDF_LAYOUTS = [
  { value: "explore", label: "Explore (30 min)" },
  { value: "focus", label: "Focus (60 min)" },
  { value: "transform", label: "Transform - 1st session" },
  { value: "transform_followup", label: "Transform - follow-up session" },
];

// How a section prints in the PDF. Must match CounselingCategoryType.PdfBlock.
const PDF_BLOCKS = [
  { value: "", label: "Auto (from the section name)" },
  { value: "concerns", label: "Concerns (ticked list)" },
  { value: "client_words", label: "Concerns in client's own words" },
  { value: "observations", label: "Observation table" },
  { value: "overview", label: "Overview paragraph" },
  { value: "strengths", label: "Strengths" },
  { value: "growth", label: "Areas for growth" },
  { value: "goals", label: "Goal setting" },
  { value: "kit", label: "Well-being kit / strategies" },
  { value: "homework", label: "Homework" },
  { value: "improved", label: "Domains that show improvement" },
  { value: "to_improve", label: "Domains that need improvement" },
  { value: "progress", label: "Progress achieved" },
  { value: "review", label: "Assignment review" },
  { value: "notes", label: "Question: answer list" },
];

const ReportTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [packages, setPackages] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [form, setForm] = useState(emptyTemplate);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  // Section ids in the order the report asks and prints them.
  const [picked, setPicked] = useState([]);
  // Section id -> PDF block ("" = worked out from the section's name).
  const [blocks, setBlocks] = useState({});

  const unwrap = (res) =>
    res?.data?.data?.results || res?.data?.data || res?.data?.results || res?.data || [];

  const fetchTemplates = () => {
    token_api
      .get("counseling/v1/report_templates/")
      .then((res) => setTemplates(unwrap(res)))
      .catch((err) => console.error("Failed to load report templates:", err));
  };

  const fetchSections = () => {
    token_api
      .get("counseling/v1/counseling_categories/")
      .then((res) => setAllSections(unwrap(res)))
      .catch((err) => console.error("Failed to load sections:", err));
  };

  const fetchPackages = () => {
    token_api
      .get("counseling/v1/counselling_packages/")
      .then((res) => setPackages(unwrap(res)))
      .catch((err) => console.error("Failed to load packages:", err));
  };

  useEffect(() => {
    fetchTemplates();
    fetchSections();
    fetchPackages();
  }, []);

  const saveTemplate = () => {
    if (!form.name) {
      alert("Report name is required.");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      report_number: Number(form.report_number) || 1,
      follow_up: !!form.follow_up,
      pdf_layout: form.pdf_layout || "explore",
      // Sent even when empty, so "Not tied to a package" can untie a report.
      package: form.package || null,
    };
    // An unset package means the report is not tied to one.
    if (form.package) payload.package = form.package;

    const request = editingId
      ? token_api.patch(`counseling/v1/report_templates/${editingId}/`, payload)
      : token_api.post("counseling/v1/report_templates/", payload);

    request
      .then((res) => {
        const saved = res?.data?.data || res?.data;
        const id = editingId || saved?.id;
        // Sections are a separate call: they reassign rows on another table.
        return token_api.post(
          `counseling/v1/report_templates/${id}/set_sections/`,
          {
            sections: picked,
            // Only the sections given a design; the rest stay as they are.
            blocks: Object.fromEntries(
              picked.filter((sid) => blocks[sid]).map((sid) => [sid, blocks[sid]])
            ),
          }
        );
      })
      .then(() => {
        setSaving(false);
        cancelEdit();
        fetchTemplates();
        fetchSections();
      })
      .catch((err) => {
        setSaving(false);
        console.error("Save error:", err?.response?.data || err);
        alert("Failed to save the report template.");
      });
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({
      name: t.name || "",
      package: t.package || "",
      report_number: t.report_number ?? 1,
      follow_up: !!t.follow_up,
      pdf_layout: t.pdf_layout || "explore",
    });
    setPicked((t.sections || []).map((s) => s.id));
    setBlocks(Object.fromEntries((t.sections || []).map((s) => [s.id, s.pdf_block || ""])));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyTemplate);
    setPicked([]);
    setBlocks({});
  };

  const deleteTemplate = (t) => {
    if (!window.confirm(`Delete "${t.name || `Report ${t.report_number}`}"?`)) return;
    token_api
      .delete(`counseling/v1/report_templates/${t.id}/`)
      .then(() => {
        if (editingId === t.id) cancelEdit();
        fetchTemplates();
        fetchSections();
      })
      .catch((err) => {
        console.error("Delete error:", err?.response?.data || err);
        alert("Failed to delete.");
      });
  };

  const toggleSection = (id) => {
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const moveSection = (index, delta) => {
    const next = [...picked];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setPicked(next);
  };

  const sectionById = (id) => allSections.find((s) => s.id === id);

  // A section belongs to one report, so show which one already claims it.
  const ownerOf = (section) => {
    if (!section.report) return null;
    const owner = templates.find((t) => t.id === section.report);
    return owner ? owner.name || `Report ${owner.report_number}` : null;
  };

  return (
    <div style={{ background: "#f5f7fb", minHeight: "100%", padding: "24px 0" }}>
      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 16px" }}>
        <Box sx={cardSx}>
          <h2 style={{ color: "#313E6C", margin: "0 0 4px" }}>
            {editingId ? "Edit Report Template" : "Create Report Template"}
          </h2>
          <p style={{ color: "#696984", margin: "0 0 20px", fontSize: 14 }}>
            A report template decides which sections a counsellor is asked to
            fill in for a session.
          </p>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Report Name *"
                size="small"
                fullWidth
                placeholder="e.g. Emotional & Behavioural Counselling - EXPLORE"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Package"
                size="small"
                fullWidth
                value={form.package}
                onChange={(e) => setForm({ ...form, package: e.target.value })}
              >
                <MenuItem value="">Not tied to a package</MenuItem>
                {/* Price and state too: two packages can share a name
                    (an old inactive "Explore" beside the live one). */}
                {packages.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} · ₹{Number(p.selling_price)}
                    {p.status === false ? " (inactive)" : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Report Number"
                type="number"
                size="small"
                fullWidth
                helperText="1st, 2nd session…"
                value={form.report_number}
                onChange={(e) =>
                  setForm({ ...form, report_number: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6} md={1}>
              <label
                style={{ display: "flex", alignItems: "center", fontSize: 14 }}
              >
                <Checkbox
                  checked={form.follow_up}
                  onChange={(e) =>
                    setForm({ ...form, follow_up: e.target.checked })
                  }
                />
                Follow-up
              </label>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="PDF Design"
                size="small"
                fullWidth
                helperText="How the printed report looks"
                value={form.pdf_layout}
                onChange={(e) => setForm({ ...form, pdf_layout: e.target.value })}
              >
                {PDF_LAYOUTS.map((l) => (
                  <MenuItem key={l.value} value={l.value}>
                    {l.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <h3 style={{ color: "#313E6C", margin: "24px 0 4px", fontSize: 16 }}>
            Sections in this report
          </h3>
          <p style={{ color: "#696984", margin: "0 0 12px", fontSize: 13 }}>
            Tick the sections this report prints. A section can only belong to
            one report, so ticking it here removes it from its current one.
          </p>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: "1px solid #eef0f4",
                  borderRadius: "10px",
                  maxHeight: 320,
                  overflowY: "auto",
                  p: 1,
                }}
              >
                {allSections.map((section) => {
                  const owner = ownerOf(section);
                  const isPicked = picked.includes(section.id);
                  return (
                    <label
                      key={section.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: 14,
                        padding: "2px 4px",
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isPicked}
                        onChange={() => toggleSection(section.id)}
                      />
                      <span>{section.title}</span>
                      {owner && !isPicked && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 12,
                            color: "#999",
                          }}
                        >
                          (in {owner})
                        </span>
                      )}
                    </label>
                  );
                })}
                {!allSections.length && (
                  <p style={{ color: "#999", fontSize: 13, padding: 8 }}>
                    No sections yet. Add them in admin under Counseling category
                    types.
                  </p>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  border: "1px solid #eef0f4",
                  borderRadius: "10px",
                  maxHeight: 320,
                  overflowY: "auto",
                  p: 1,
                }}
              >
                <p
                  style={{
                    margin: "4px 8px 8px",
                    fontSize: 13,
                    color: "#696984",
                  }}
                >
                  Order the counsellor fills them in, and the PDF prints
                  them in. The PDF heading is the section's name; the
                  design sets how it prints.
                </p>
                {picked.map((id, index) => {
                  const section = sectionById(id);
                  if (!section) return null;
                  return (
                    <div
                      key={id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: 14,
                        padding: "2px 8px",
                      }}
                    >
                      <span style={{ width: 22, color: "#999" }}>
                        {index + 1}.
                      </span>
                      <span style={{ flex: 1 }}>{section.title}</span>
                      <TextField
                        select
                        size="small"
                        variant="standard"
                        value={blocks[id] ?? section.pdf_block ?? ""}
                        onChange={(e) => setBlocks({ ...blocks, [id]: e.target.value })}
                        sx={{ width: 190, mx: 1, "& .MuiInputBase-input": { fontSize: 12 } }}
                        SelectProps={{ displayEmpty: true }}
                      >
                        {PDF_BLOCKS.map((b) => (
                          <MenuItem key={b.value} value={b.value} sx={{ fontSize: 13 }}>
                            {b.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <IconButton
                        size="small"
                        onClick={() => moveSection(index, -1)}
                        disabled={index === 0}
                      >
                        <ArrowUpwardIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => moveSection(index, 1)}
                        disabled={index === picked.length - 1}
                      >
                        <ArrowDownwardIcon fontSize="inherit" />
                      </IconButton>
                    </div>
                  );
                })}
                {!picked.length && (
                  <p style={{ color: "#999", fontSize: 13, padding: 8 }}>
                    Nothing picked yet.
                  </p>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              onClick={saveTemplate}
              disabled={saving}
              style={{ backgroundColor: "#313E6C" }}
            >
              {saving ? "Saving…" : editingId ? "Save Changes" : "Create Report"}
            </Button>
            {editingId && (
              <Button variant="outlined" onClick={cancelEdit}>
                Cancel
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={cardSx}>
          <h2 style={{ color: "#313E6C", margin: "0 0 20px" }}>
            Report Templates ({templates.length})
          </h2>

          {templates.map((t) => (
            <Box
              key={t.id}
              sx={{
                border: "1px solid #eef0f4",
                borderRadius: "10px",
                p: 2,
                mb: 2,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <strong style={{ color: "#313E6C" }}>
                    {t.name || `Report ${t.report_number}`}
                  </strong>
                  <div style={{ fontSize: 13, color: "#696984", marginTop: 2 }}>
                    Report {t.report_number}
                    {t.follow_up ? " · follow-up" : ""}
                    {" · "}
                    {(PDF_LAYOUTS.find((l) => l.value === t.pdf_layout) || PDF_LAYOUTS[0]).label}
                    {" · "}
                    {(t.sections || []).length} sections
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button size="small" onClick={() => handleEdit(t)}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => deleteTemplate(t)}
                  >
                    Delete
                  </Button>
                </div>
              </div>

              <ol
                style={{
                  margin: "10px 0 0",
                  paddingLeft: 22,
                  fontSize: 14,
                  color: "#444",
                }}
              >
                {(t.sections || []).map((s) => (
                  <li key={s.id}>
                    {s.title}
                    {s.ai && (
                      <span style={{ color: "#1F7A78", fontSize: 12 }}> · AI</span>
                    )}
                  </li>
                ))}
              </ol>
            </Box>
          ))}

          {!templates.length && (
            <p style={{ color: "#999", fontSize: 14 }}>
              No report templates yet. Create one above.
            </p>
          )}
        </Box>
      </div>
    </div>
  );
};

export default ReportTemplates;
